/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react'
import { useTranslations } from 'next-intl'
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  chakra,
  Text,
  Flex,
  Box,
  useColorModeValue,
  TableContainer,
} from '@chakra-ui/react'
import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons'
import {
  useReactTable,
  flexRender,
  getCoreRowModel,
  ColumnDef,
  SortingState,
  getSortedRowModel,
} from '@tanstack/react-table'
import { IMinerNode } from '@/types/miner.interface'
import { formatPrice } from '@/utils/formatPrice'
import getHashrateValue from '@/utils/getHashrateValue'
import { getHardwareOnlineDate } from '@/utils/getHardwareOnlineDate'
import { format, parseISO } from 'date-fns'
import { isCurrentDateHigher } from '@/utils/date'
import { getDaysLeft } from '@/utils/getDaysLeft'
import { RelocationTooltip } from '@/features/hardware/RelocationTooltip'
import { AddToCartCell } from './AddtoCartCell'
import { Pagination } from '../Pagination'

export type ProductsListTableProps<Data extends object> = {
  data: IMinerNode[]
  columns: ColumnDef<Data, any>[]
  hasTableOverflow?: boolean
  showPriceOnly?: boolean
  enableSortingRemoval?: boolean
  paymentPlanButton?: React.ReactNode
  refetch?: any
  totalCount?: any
}

export function ProductsListTable<Data extends object>({
  data,
  columns,
  hasTableOverflow,
  showPriceOnly,
  enableSortingRemoval = false,
  paymentPlanButton = null,
  refetch,
  totalCount,
}: ProductsListTableProps<Data>) {
  const t = useTranslations()
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'productListingPrice', desc: false },
  ])

  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: enableSortingRemoval,
    state: {
      sorting,
    },
  })
  const theadBg = useColorModeValue('white', '#181818')
  const textColor = useColorModeValue('black', 'white')

  const isNonhostedProduct = (item: IMinerNode) => {
    if (item?.productType === 'NON_HOSTED') {
      return true
    }
    return false
  }

  const getStock = (stock: number) => {
    if (stock > 10) {
      return '10+'
    }
    return stock
  }

  const getWarrantyDate = (date: string) => {
    if (date) {
      return isCurrentDateHigher(date)
        ? t('dashboard.machine-sale.details.expired')
        : format(parseISO(date), 'MM/dd/yyyy')
    } else {
      return '-'
    }
  }

  const getWarrantyStatus = (warrantyAvailable: boolean | undefined) => {
    if (warrantyAvailable) {
      return '✓'
    }
    return '-'
  }

  const shouldHideColumn = data.every((product) => {
    if (product.productType === 'HOSTED') {
      return (
        product.hostedListingInWarranty === null ||
        !product.productAvailableQuantity
      )
    }
    return (
      product.minerModelManufacturerWarrantyExp === null ||
      product.productAvailableQuantity > 1
    )
  })

  return (
    <TableContainer
      w="full"
      borderRadius="8px"
      borderWidth={1}
      sx={
        totalCount > 10
          ? {}
          : { 'tbody tr:last-of-type td': { borderBottomColor: 'transparent' } }
      }
    >
      <Table>
        <Thead bg={theadBg}>
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id} cursor={'pointer'}>
              {headerGroup.headers.map((header) => {
                if (
                  header.id === 'minerModelManufacturerWarrantyExp' &&
                  shouldHideColumn
                )
                  return null

                return (
                  <Th
                    key={header.id}
                    onClick={header.column.getToggleSortingHandler()}
                    py="12px"
                    pl="16px"
                    pr="10px"
                    fontSize="12px"
                    color={textColor}
                    textTransform="none"
                    fontWeight="600"
                    bg={theadBg}
                    w="auto"
                    textAlign="left"
                    _hover={{
                      '& .sort-icon': {
                        opacity: 1,
                      },
                    }}
                    className={
                      header.id === 'productListingPrice' && !showPriceOnly
                        ? 'sticky'
                        : ''
                    }
                    css={{
                      '&.sticky': {
                        position: 'sticky',
                        right: 0,
                        zIndex: 10,
                      },
                    }}
                  >
                    <Flex alignItems={'center'} justifyContent={'flex-start'}>
                      <Box>
                        {header.id !== 'hostedTargetOnlineDate'
                          ? flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )
                          : isNonhostedProduct(data[0])
                          ? t('hardware-list.labels.est-shipping-date')
                          : t('hardware-list.labels.est-online-date')}
                      </Box>

                      {header.id !== 'productId' && (
                        <chakra.span
                          className="sort-icon"
                          opacity={header.column.getIsSorted() ? 1 : 0}
                          ml={
                            header.id === 'productAvailableQuantity' ||
                            header.id === 'hostedTargetOnlineDate'
                              ? 0
                              : 2
                          }
                        >
                          {header.column.getIsSorted() === 'desc' ? (
                            <TriangleUpIcon
                              color={'brand.600'}
                              aria-label="sorted descending"
                            />
                          ) : (
                            <TriangleDownIcon
                              color={'brand.600'}
                              aria-label="sorted ascending"
                            />
                          )}
                        </chakra.span>
                      )}
                    </Flex>
                  </Th>
                )
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody bg={theadBg}>
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id} h={75}>
              {row.getVisibleCells().map((cell) => {
                const rowData = cell.row.original

                return (
                  <React.Fragment key={cell.id}>
                    {cell.column.id === 'facilityDisplayName' ? (
                      <Td key={cell.id} textAlign={'left'} py="12px" px="16px">
                        <Box
                          fontSize="16px"
                          maxW="150px"
                          wordBreak="normal"
                          whiteSpace="normal"
                        >
                          {rowData.productType === 'NON_HOSTED'
                            ? t('hardware-list.labels.delivery')
                            : `${rowData.facilityDisplayName}, ${
                                rowData.countryName === 'United States'
                                  ? 'US'
                                  : rowData.countryName
                              }`}
                          <RelocationTooltip facilityId={rowData.facilityId} />
                        </Box>
                      </Td>
                    ) : null}
                    {cell.column.id === 'minerModelName' ? (
                      <Td
                        key={cell.id}
                        textAlign={'left'}
                        py="12px"
                        px="16px"
                        fontSize="16px"
                      >
                        <Text size="16px">{rowData?.minerModelName}</Text>
                      </Td>
                    ) : null}
                    {cell.column.id === 'productCondition' ? (
                      <Td
                        key={cell.id}
                        py="4"
                        px="4"
                        fontSize="16px"
                        textAlign="left"
                        fontWeight={'normal'}
                      >
                        <Text>{rowData?.productCondition}</Text>
                      </Td>
                    ) : null}
                    {cell.column.id === 'hostedTargetOnlineDate' ? (
                      <Td
                        key={cell.id}
                        py="12px"
                        px="16px"
                        fontSize="16px"
                        fontWeight="400"
                      >
                        <Text>
                          {getHardwareOnlineDate(
                            rowData.productType,
                            rowData.nonhostedEarliestShippingDate,
                            rowData.hostedTargetOnlineDate,
                            t('common.labels.not-applicable'),
                            t('checkout.non-hosted-est-shipping-date')
                          )}
                        </Text>
                      </Td>
                    ) : null}
                    {cell.column.id === 'productAvailableQuantity' ? (
                      <Td
                        key={cell.id}
                        py="12px"
                        px="16px"
                        fontSize="16px"
                        textAlign="left"
                        fontWeight="400"
                      >
                        <Text>
                          {getStock(rowData?.productAvailableQuantity)}
                        </Text>
                      </Td>
                    ) : null}
                    {cell.column.id === 'monthlyHostingCharges' ? (
                      <Td
                        key={cell.id}
                        py="12px"
                        px="16px"
                        fontWeight="400"
                        fontSize="16px"
                        textAlign={'left'}
                      >
                        {!isNonhostedProduct(rowData) ? (
                          <Text>
                            {formatPrice(
                              rowData?.overriddenMonthlyHostingCharges ||
                                rowData?.monthlyHostingCharges,
                              true
                            )}
                          </Text>
                        ) : (
                          <Text>{t('common.labels.not-applicable')}</Text>
                        )}
                      </Td>
                    ) : null}
                    {cell.column.id === 'minerModelHashratePerTerahash' ? (
                      <Td
                        key={cell.id}
                        py="4"
                        fontSize="16px"
                        textAlign="left"
                        fontWeight={'normal'}
                        px={4}
                      >
                        <Text>
                          {getHashrateValue(
                            rowData?.minerModelAlgorithm,
                            Number(rowData?.minerModelHashratePerTerahash),
                            false
                          )}
                        </Text>
                      </Td>
                    ) : null}
                    {cell.column.id === 'minerModelPowerConsumptionWatts' ? (
                      <Td
                        key={cell.id}
                        py="4"
                        fontSize="16px"
                        fontWeight={'normal'}
                        px={4}
                        textAlign="left"
                      >
                        <Text>{`${rowData?.minerModelPowerConsumptionWatts.toLocaleString()} Watts`}</Text>
                      </Td>
                    ) : null}
                    {cell.column.id === 'productDateListed' ? (
                      <Td
                        key={cell.id}
                        py="12px"
                        px="16px"
                        fontSize="16px"
                        fontWeight="400"
                        textAlign="left"
                      >
                        <Text>{getDaysLeft(rowData?.productDateListed)}</Text>
                      </Td>
                    ) : null}
                    {cell.column.id === 'productPricePerHashrate' ? (
                      <Td
                        key={cell.id}
                        py="4"
                        fontSize="16px"
                        textAlign="left"
                        fontWeight="400"
                        px={4}
                      >
                        <Text>
                          {getHashrateValue(
                            rowData?.minerModelAlgorithm,
                            Number(rowData?.productPricePerHashrate),
                            true
                          )}
                        </Text>
                      </Td>
                    ) : null}
                    {cell.column.id === 'minerFirmware' ? (
                      <Td
                        key={cell.id}
                        py="12px"
                        px="16px"
                        fontSize="16px"
                        fontWeight="400"
                        textAlign="left"
                      >
                        <Text>
                          {rowData?.minerFirmware ?? rowData?.manufacturerName}
                        </Text>
                      </Td>
                    ) : null}
                    {cell.column.id === 'minerModelManufacturerWarrantyExp' &&
                    !shouldHideColumn ? (
                      <Td
                        key={cell.id}
                        py="4"
                        fontSize="16px"
                        textAlign="left"
                        fontWeight="400"
                        px={4}
                      >
                        <Text>
                          {rowData.productType === 'HOSTED'
                            ? getWarrantyStatus(
                                rowData?.hostedListingInWarranty
                              )
                            : getWarrantyDate(
                                rowData?.minerModelManufacturerWarrantyExp
                              )}
                        </Text>
                      </Td>
                    ) : null}
                    {cell.column.id === 'parentFee' ? (
                      <Td
                        key={cell.id}
                        py="4"
                        fontSize={'xs'}
                        textAlign="left"
                        fontWeight="semibold"
                        px={4}
                      >
                        <Text>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                          /TH
                        </Text>
                      </Td>
                    ) : null}
                    {cell?.column?.id === 'productListingPrice' &&
                    showPriceOnly ? (
                      <Td
                        key={cell.id}
                        py="12px"
                        px="16px"
                        textAlign="left"
                        fontWeight="400"
                        fontSize="16px"
                        className="price-column-cell"
                      >
                        <Text>
                          {formatPrice(rowData.productListingPrice, false)}
                        </Text>
                      </Td>
                    ) : null}
                    {cell?.column?.id === 'productListingPrice' &&
                    !showPriceOnly ? (
                      <Td
                        key={cell.id}
                        textAlign="left"
                        py="12px"
                        px="16px"
                        position="sticky"
                        right="0"
                        fontWeight="400"
                        fontSize="16px"
                        className="price-column-cell"
                        bg={theadBg}
                        zIndex={10}
                        minW="230px"
                        maxW="230px"
                        boxShadow={
                          hasTableOverflow
                            ? 'inset 2px 0px 8px 0px rgba(0, 0, 0, 0.05)'
                            : ''
                        }
                      >
                        <AddToCartCell rowData={rowData} />
                      </Td>
                    ) : null}
                  </React.Fragment>
                )
              })}
            </Tr>
          ))}
        </Tbody>
      </Table>
      {totalCount > 10 && (
        <Box bg={theadBg} py={2}>
          <Pagination
            refetch={refetch}
            totalCount={totalCount}
            perPageLabel={t('dashboard.machine-sale.sidebar.listings-per-page')}
          />
        </Box>
      )}
    </TableContainer>
  )
}
